/**
 * Session persistence service — saves/restores research sessions to Supabase.
 * Sessions are scoped to the authenticated Supabase user when logged in,
 * falling back to a localStorage UUID for unauthenticated use.
 * This enables cross-device sync and persistence across browser restarts.
 */

import { createClient } from '@/lib/supabase/client';

const SESSION_ID_KEY = 'orbital_session_id';

function getLocalSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(SESSION_ID_KEY);
}

function setLocalSessionId(id: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SESSION_ID_KEY, id);
}

/**
 * Get the current authenticated user's ID, or null if not authenticated.
 */
async function getAuthUserId(): Promise<string | null> {
  try {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Restore all relevant localStorage keys from a DB session object.
 * Called after loading a session from Supabase so the app can resume
 * exactly where it left off, even after a browser restart.
 */
export function restoreSessionToLocalStorage(session: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  // Restore industry
  if (session.industry) {
    localStorage.setItem('orbital_industry', session.industry as string);
    localStorage.setItem('on_industry_full', JSON.stringify({ name: session.industry }));
  }

  // Restore discovery results + scan meta
  if (session.discovery) {
    const disc = session.discovery as Record<string, unknown>;
    if (disc.results && Array.isArray(disc.results) && disc.results.length > 0) {
      localStorage.setItem('on_discovery_results', JSON.stringify(disc.results));
    }
    if (disc.meta) {
      localStorage.setItem('last_scan_meta', JSON.stringify(disc.meta));
    }
  }

  // Restore entry point
  if (session.entry) {
    const entryVal = typeof session.entry === 'string'
      ? session.entry
      : JSON.stringify(session.entry);
    localStorage.setItem('on_entry', entryVal);
    localStorage.setItem('orbital_entry', entryVal);
  }

  // Restore market size
  if (session.market_size) {
    const marketVal = typeof session.market_size === 'string'
      ? session.market_size
      : JSON.stringify(session.market_size);
    localStorage.setItem('on_market', marketVal);
  }

  // Restore validation
  if (session.validation) {
    const valVal = typeof session.validation === 'string'
      ? session.validation
      : JSON.stringify(session.validation);
    localStorage.setItem('on_validation', valVal);
    localStorage.setItem('orbital_validation', valVal);
  }

  // Restore execution plan
  if (session.execution) {
    const execVal = typeof session.execution === 'string'
      ? session.execution
      : JSON.stringify(session.execution);
    localStorage.setItem('on_execution', execVal);
    const execObj = typeof session.execution === 'string'
      ? JSON.parse(session.execution)
      : session.execution as Record<string, unknown>;
    if (execObj.hypothesis) localStorage.setItem('orbital_hypothesis', execObj.hypothesis as string);
    if (execObj.mvp) localStorage.setItem('orbital_mvp', JSON.stringify(execObj.mvp));
    if (execObj.monetization) localStorage.setItem('orbital_monetization', JSON.stringify(execObj.monetization));
    if (execObj.gtm) localStorage.setItem('orbital_gtm', JSON.stringify(execObj.gtm));
    if (execObj.kill_criteria) localStorage.setItem('orbital_kill', JSON.stringify(execObj.kill_criteria));
    if (execObj.hypothesis && execObj.mvp) {
      localStorage.setItem('orbital_loaded_from_history', '1');
    }
  }
}

/**
 * Upsert a partial session update.
 * - If authenticated: sessions are scoped to the user (cross-device sync).
 * - If not authenticated: falls back to localStorage UUID tracking.
 * Returns the session ID on success.
 */
export async function saveSessionField(
  fields: Record<string, unknown>
): Promise<string | null> {
  try {
    console.log('=== saveSessionField called ===')
    console.log('Fields:', JSON.stringify(fields).slice(0, 200))
    console.log('Existing session ID:', localStorage.getItem('orbital_session_id'))

    const userId = await getAuthUserId();
    const existingId = getLocalSessionId();

    if (existingId) {
      // Update existing session
      const res = await fetch(`/api/sessions/${existingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      console.log('Save response status:', res.status)
      if (!res.ok) {
        console.warn('Session PATCH failed, will retry as new session');
        // Fall through to create new session
      } else {
        const json = await res.json().catch(() => ({}));
        console.log('Save response:', JSON.stringify(json).slice(0, 200))
        return existingId;
      }
    }

    // Create new session — API route attaches user_id if authenticated
    const payload: Record<string, unknown> = {
      ...fields,
      created_at: new Date().toISOString(),
    };
    if (userId) {
      payload.user_id = userId;
    }

    const res = await fetch('/api/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    console.log('Save response status:', res.status)
    if (!res.ok) throw new Error('Session POST failed');
    const json = await res.json();
    console.log('Save response:', JSON.stringify(json).slice(0, 200))
    if (json?.id) {
      setLocalSessionId(json.id);
      return json.id;
    }
    return null;
  } catch (err) {
    console.warn('Session save error (non-fatal):', err);
    return null;
  }
}

/**
 * Load the most recent session for the current user and restore it to localStorage.
 * - If authenticated: fetches the latest session from Supabase (cross-device).
 * - If not authenticated: loads by the localStorage session ID.
 * Returns null if no session found.
 */
export async function loadCurrentSession(): Promise<Record<string, unknown> | null> {
  try {
    const userId = await getAuthUserId();

    if (userId) {
      // Authenticated: fetch the most recent session for this user
      const res = await fetch('/api/sessions');
      if (!res.ok) return null;
      const sessions = await res.json();
      if (Array.isArray(sessions) && sessions.length > 0) {
        const latest = sessions[0];
        // Sync local session ID to the latest server session
        if (latest.id) setLocalSessionId(latest.id);
        // Restore all localStorage keys from DB session
        restoreSessionToLocalStorage(latest);
        return latest;
      }
      return null;
    }

    // Unauthenticated: use localStorage session ID
    const id = getLocalSessionId();
    if (!id) return null;
    const res = await fetch(`/api/sessions/${id}`);
    if (!res.ok) return null;
    const session = await res.json();
    // Restore all localStorage keys from DB session
    restoreSessionToLocalStorage(session);
    return session;
  } catch {
    return null;
  }
}

/**
 * Clear the current session ID from localStorage (start fresh).
 */
export function clearSessionId(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(SESSION_ID_KEY);
  }
}
